import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import tenantService from '@/lib/tenant.service';
import type { Tenant, TenantSettings } from '@/lib/tenant.service';

export const tenantKeys = {
  all: ['tenants'] as const,
  list: () => [...tenantKeys.all, 'list'] as const,
  detail: (id: string) => [...tenantKeys.all, 'detail', id] as const,
};

// `enabled` lets pages that only need the list for super admins skip the
// call for everyone else (the endpoint is super-admin-only).
export function useTenants(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: tenantKeys.list(),
    queryFn: tenantService.list,
    enabled: options?.enabled ?? true,
  });
}

export function useTenant(id: string) {
  return useQuery({
    queryKey: tenantKeys.detail(id),
    queryFn: () => tenantService.get(id),
    enabled: !!id,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Tenant>) => tenantService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.list() });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tenant> }) =>
      tenantService.update(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.list() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(variables.id) });
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tenantService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.list() });
    },
  });
}

export function useSetTenantActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      tenantService.setActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.list() });
    },
  });
}

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, settings }: { id: string; settings: TenantSettings }) =>
      tenantService.updateSettings(id, settings),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.list() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(variables.id) });
    },
  });
}
